import type { AppEvent } from '../core/types.js';
import type { InsightCardData } from '../components/insight-card.js';
import { totalSkippedPurchase } from './money.js';

export interface InsightInput {
  events: AppEvent[];
  /** Local date cutoff for "this week", inclusive. */
  weekStartIso: string;
  weekEndIso: string;
  priorWeekStartIso: string;
  priorWeekEndIso: string;
  /** Habit unit (e.g. "use", "drink"). Used in copy. */
  unit: string;
  /** Habit domain for safety-tinted copy. */
  domain?: string;
}

/**
 * Generate insight cards for the Progress screen. Cards are ordered by
 * priority. Returns at most 4 cards to keep the screen scannable.
 */
export function buildInsightCards(input: InsightInput): InsightCardData[] {
  const cards: InsightCardData[] = [];
  const active = input.events.filter((e) => e.status !== 'undone');
  const thisWeek = active.filter((e) => e.occurredAt >= input.weekStartIso && e.occurredAt <= input.weekEndIso);
  const priorWeek = active.filter((e) => e.occurredAt >= input.priorWeekStartIso && e.occurredAt < input.weekStartIso);

  // Low-data check: under 3 events in the last 2 weeks → nudge logging.
  if (thisWeek.length + priorWeek.length < 3) {
    cards.push({
      id: 'low_data',
      kind: 'low_data',
      headline: 'Not enough data yet.',
      evidence: 'Log a few uses or waits so we can show patterns. Tracking is private.',
    });
    return cards;
  }

  // Win — urges passed this week.
  const passed = thisWeek.filter((e) => e.type === 'success_logged'
    && (e.payload as { kind?: string }).kind === 'urge_passed').length;
  const challengeMet = thisWeek.filter((e) => e.type === 'success_logged'
    && (e.payload as { challengeMet?: boolean }).challengeMet === true).length;
  if (passed > 0) {
    const w = passed === 1 ? 'urge' : 'urges';
    cards.push({
      id: 'win_urges',
      kind: 'win',
      headline: `${passed} ${w} beaten this week.`,
      ...(challengeMet > 0 ? { evidence: `${challengeMet} happened after a challenge — that\'s real practice.` } : {}),
    });
  }

  // Money protected this week.
  const protectedTotals = totalSkippedPurchase(thisWeek, input.weekStartIso);
  if (protectedTotals.count > 0 && protectedTotals.totalProtected > 0) {
    cards.push({
      id: 'win_money',
      kind: 'win',
      headline: `Protected ${protectedTotals.currency} ${protectedTotals.totalProtected.toFixed(2)} this week.`,
      evidence: `${protectedTotals.count} skipped purchase${protectedTotals.count === 1 ? '' : 's'} logged.`,
    });
  }

  // Pattern — usage trend vs prior week.
  const usesThis = thisWeek.filter((e) => e.type === 'usage_logged').length;
  const usesPrior = priorWeek.filter((e) => e.type === 'usage_logged').length;
  if (usesPrior > 0) {
    const delta = usesThis - usesPrior;
    const pct = Math.round(Math.abs(delta) / usesPrior * 100);
    if (delta < 0 && pct >= 5) {
      cards.push({
        id: 'pattern_down',
        kind: 'pattern',
        headline: `${pct}% fewer ${input.unit}s than last week.`,
        evidence: `${usesPrior} → ${usesThis}.`,
      });
    } else if (delta > 0 && pct >= 25) {
      cards.push({
        id: 'pattern_up',
        kind: 'bad_week',
        headline: `Tougher week — ${pct}% more ${input.unit}s than last.`,
        evidence: `${usesPrior} → ${usesThis}. A harder week is data, not failure.`,
      });
    }
  }

  // Average wait-before-use across used episodes.
  const usedWithWait = thisWeek.filter((e) => e.type === 'usage_logged'
    && typeof (e.payload as { waitedMsBeforeUse?: number }).waitedMsBeforeUse === 'number');
  if (usedWithWait.length > 0) {
    const sum = usedWithWait.reduce((acc, e) =>
      acc + ((e.payload as { waitedMsBeforeUse: number }).waitedMsBeforeUse), 0);
    const avgMin = Math.round(sum / usedWithWait.length / 60000);
    if (avgMin >= 1) {
      cards.push({
        id: 'pattern_wait_before_use',
        kind: 'pattern',
        headline: `Average ${avgMin} min waited before use.`,
        evidence: 'Waiting builds tolerance even when you use afterward.',
      });
    }
  }

  // Rescue effectiveness: which module helped most often this week.
  const rescueHelp = detectRescueEffectiveness(thisWeek);
  if (rescueHelp) cards.push(rescueHelp);

  // Intensity trend: compare average start-intensity this week vs prior.
  const intensity = detectIntensityTrend(thisWeek, priorWeek);
  if (intensity) cards.push(intensity);

  // Time-window pattern insight: detect when usage clusters in a narrow
  // window (e.g. 8-11 PM). Doc 09 calls this the "hardest window" pattern.
  const timeWindow = detectTimeWindow(thisWeek);
  if (timeWindow) cards.push(timeWindow);

  // Trigger pattern insight: detect when a configured trigger shows up
  // repeatedly on urge events.
  const triggerInsight = detectTriggerCluster(thisWeek);
  if (triggerInsight) cards.push(triggerInsight);

  // Milestones — recent firsts. Only fire when the most recent qualifying
  // event happened this week, so the celebration is timely.
  const milestone = detectMilestone(active, input.weekStartIso);
  if (milestone) cards.push(milestone);

  // Next action prompt when we have signal but no obvious win.
  if (cards.length === 0) {
    cards.push({
      id: 'next_action',
      kind: 'next_action',
      headline: 'Keep logging — patterns become clearer with more data.',
      evidence: 'Try a urge timer the next time a craving hits.',
    });
  }
  return cards.slice(0, 4);
}

/** Pull intensityStart values off urge-linked outcome events. */
function intensityStarts(events: AppEvent[]): number[] {
  const out: number[] = [];
  for (const e of events) {
    if (e.type !== 'success_logged' && e.type !== 'usage_logged' && e.type !== 'urge_episode_closed') continue;
    const v = (e.payload as { intensityStart?: number }).intensityStart;
    if (typeof v === 'number') out.push(v);
  }
  return out;
}

function average(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/**
 * Returns a card when at least 3 intensity ratings exist this week. Compares
 * the weekly average to the prior week (if available) and reports trend.
 */
function detectIntensityTrend(thisWeek: AppEvent[], priorWeek: AppEvent[]): InsightCardData | null {
  const now = intensityStarts(thisWeek);
  if (now.length < 3) return null;
  const avgNow = average(now);
  const prior = intensityStarts(priorWeek);
  if (prior.length < 3) {
    return {
      id: 'intensity_baseline',
      kind: 'pattern',
      headline: `Average urge intensity this week: ${avgNow.toFixed(1)} of 5.`,
      evidence: `${now.length} ratings. We will compare next week.`,
    };
  }
  const avgPrior = average(prior);
  const delta = avgNow - avgPrior;
  if (Math.abs(delta) < 0.4) {
    return {
      id: 'intensity_steady',
      kind: 'pattern',
      headline: `Urge intensity holding around ${avgNow.toFixed(1)} of 5.`,
      evidence: `Prior week ${avgPrior.toFixed(1)}, this week ${avgNow.toFixed(1)}.`,
    };
  }
  if (delta < 0) {
    return {
      id: 'intensity_down',
      kind: 'win',
      headline: `Urges ${Math.abs(delta).toFixed(1)} points easier this week.`,
      evidence: `Prior week ${avgPrior.toFixed(1)}, this week ${avgNow.toFixed(1)}.`,
    };
  }
  return {
    id: 'intensity_up',
    kind: 'bad_week',
    headline: `Urges ${delta.toFixed(1)} points harder this week.`,
    evidence: `Prior week ${avgPrior.toFixed(1)}, this week ${avgNow.toFixed(1)}. A harder week is still data.`,
  };
}

/**
 * Returns a "rescue helped" card when a module shows up on episodes that
 * ended with urge_passed. Groups events by episodeId and attributes each
 * passed episode to every distinct module run inside it.
 */
function detectRescueEffectiveness(events: AppEvent[]): InsightCardData | null {
  const byEpisode = new Map<string, { passed: boolean; modules: Set<string> }>();
  for (const e of events) {
    const epId = e.episodeId;
    if (!epId) continue;
    const entry = byEpisode.get(epId) ?? { passed: false, modules: new Set<string>() };
    if (e.type === 'success_logged' && (e.payload as { kind?: string }).kind === 'urge_passed') {
      entry.passed = true;
    } else if (e.type === 'urge_episode_closed' && (e.payload as { outcome?: string }).outcome === 'passed') {
      entry.passed = true;
    } else if (e.type === 'rescue_completed') {
      const moduleId = (e.payload as { moduleId?: string }).moduleId;
      if (moduleId) entry.modules.add(moduleId);
    }
    byEpisode.set(epId, entry);
  }
  const counts = new Map<string, number>();
  for (const entry of byEpisode.values()) {
    if (!entry.passed) continue;
    for (const m of entry.modules) counts.set(m, (counts.get(m) ?? 0) + 1);
  }
  const top = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
  if (!top || top[1] < 2) return null;
  const [moduleId, count] = top;
  const niceName = humanizeModuleId(moduleId);
  return {
    id: `pattern_rescue_${moduleId}`,
    kind: 'win',
    headline: `${niceName} helped ${count} times this week.`,
    evidence: 'Counts episodes where this module ran and the urge passed.',
  };
}

function humanizeModuleId(id: string): string {
  return id.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Returns a pattern card if at least 3 usage events fall within a 3-hour
 * local-time window. Doc 09 calls this the "hardest window" pattern.
 */
function detectTimeWindow(events: AppEvent[]): InsightCardData | null {
  const uses = events.filter((e) => e.type === 'usage_logged');
  if (uses.length < 3) return null;
  const hours = uses.map((e) => new Date(e.occurredAt).getHours());
  const counts = new Array<number>(24).fill(0);
  for (const h of hours) counts[h] = (counts[h] ?? 0) + 1;
  // Find best 3-hour window by sliding sum.
  let bestStart = 0;
  let bestSum = 0;
  for (let start = 0; start < 24; start++) {
    const sum = (counts[start] ?? 0) + (counts[(start + 1) % 24] ?? 0) + (counts[(start + 2) % 24] ?? 0);
    if (sum > bestSum) { bestSum = sum; bestStart = start; }
  }
  if (bestSum < 3) return null;
  if (bestSum / uses.length < 0.6) return null;
  const startLabel = formatHour(bestStart);
  const endLabel = formatHour((bestStart + 3) % 24);
  return {
    id: `pattern_window_${bestStart}`,
    kind: 'pattern',
    headline: `Hardest window looks like ${startLabel}–${endLabel}.`,
    evidence: `${bestSum} of ${uses.length} uses this week landed in that window.`,
  };
}

function formatHour(h: number): string {
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hh = ((h + 11) % 12) + 1;
  return `${hh} ${ampm}`;
}

/**
 * Returns a trigger-cluster card if at least 3 urge events this week share
 * a trigger tag. Reads payload.triggers from urge_logged events.
 */
function detectTriggerCluster(events: AppEvent[]): InsightCardData | null {
  const urgeEvents = events.filter((e) => e.type === 'urge_logged' || e.type === 'urge_episode_started');
  if (urgeEvents.length < 3) return null;
  const counts = new Map<string, number>();
  for (const e of urgeEvents) {
    const triggers = (e.payload as { triggers?: string[] }).triggers;
    if (!Array.isArray(triggers)) continue;
    for (const t of triggers) counts.set(t, (counts.get(t) ?? 0) + 1);
  }
  const top = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
  if (!top || top[1] < 3) return null;
  const [trigger, count] = top;
  const niceTrigger = trigger.replace(/_/g, ' ');
  return {
    id: `pattern_trigger_${trigger}`,
    kind: 'pattern',
    headline: `${capitalize(niceTrigger)} showed up in ${count} urges this week.`,
    evidence: 'A short calming rescue may work better than a distraction-only one when this trigger hits.',
  };
}

function capitalize(s: string): string {
  return s.length > 0 ? s[0]!.toUpperCase() + s.slice(1) : s;
}

/**
 * Returns a milestone card if a "first" qualifying event happened this week.
 * Looks at the user's earliest matching event to decide which milestone is
 * still fresh.
 */
function detectMilestone(events: AppEvent[], weekStartIso: string): InsightCardData | null {
  const sorted = [...events].sort((a, b) => a.occurredAt.localeCompare(b.occurredAt));
  const findFirst = (predicate: (e: AppEvent) => boolean): AppEvent | undefined =>
    sorted.find(predicate);
  const checks: Array<[InsightCardData['id'], string, (e: AppEvent) => boolean]> = [
    ['ms_first_urge_passed', 'First urge beaten — that\'s the loop in action.',
      (e) => e.type === 'success_logged' && (e.payload as { kind?: string }).kind === 'urge_passed'],
    ['ms_first_skipped_purchase', 'First skipped purchase logged — money protected.',
      (e) => e.type === 'success_logged' && (e.payload as { kind?: string }).kind === 'skipped_purchase'],
    ['ms_first_rescue_completed', 'First rescue completed — practice counts.',
      (e) => e.type === 'rescue_completed'],
    ['ms_first_waited_use', 'First "waited but used" log — that\'s honest data.',
      (e) => e.type === 'usage_logged'
        && typeof (e.payload as { waitedMsBeforeUse?: number }).waitedMsBeforeUse === 'number'],
    ['ms_first_challenge_accepted', 'First "one more challenge" — you stayed in the loop.',
      (e) => e.type === 'urge_challenge_accepted'],
    ['ms_first_challenge_assisted_pass',
      'First challenge-assisted urge passed — escalation worked.',
      (e) => e.type === 'urge_episode_closed'
        && (e.payload as { outcome?: string; challengeId?: string }).outcome === 'passed'
        && typeof (e.payload as { challengeId?: string }).challengeId === 'string'],
    ['ms_first_challenge_assisted_waited_use',
      'First challenge-then-used log — still useful delay data.',
      (e) => e.type === 'usage_logged'
        && typeof (e.payload as { challengeId?: string }).challengeId === 'string'],
    ['ms_first_plan_adjusted', 'First plan adjustment applied — Wean is learning with you.',
      (e) => e.type === 'plan_adjusted'
        && ['increase_challenge', 'decrease_challenge'].includes((e.payload as { kind?: string }).kind ?? '')],
    ['ms_first_graduated', 'Plan graduated — maintenance mode.',
      (e) => e.type === 'plan_graduated'],
    ['ms_first_return_after_absence', 'Came back after a pause — that counts.',
      (e) => e.type === 'plan_resumed'],
  ];
  for (const [id, headline, predicate] of checks) {
    const first = findFirst(predicate);
    if (first && first.occurredAt >= weekStartIso) {
      return { id, kind: 'win', headline };
    }
  }
  return null;
}

export function humanEventLabel(type: AppEvent['type'], payload: Record<string, unknown>): string {
  switch (type) {
    case 'usage_logged': {
      const wait = payload['waitedMsBeforeUse'];
      if (typeof wait === 'number' && wait > 60000) {
        return `Used after ${Math.round(wait / 60000)} min wait`;
      }
      return 'Logged use';
    }
    case 'success_logged': {
      const kind = payload['kind'];
      if (kind === 'urge_passed') return 'Beat an urge';
      if (kind === 'waited') return 'Waited it out';
      if (kind === 'skipped_purchase') return 'Skipped a purchase';
      if (kind === 'used_less') return 'Used less than usual';
      if (kind === 'stayed_in_window') return 'Stayed in the window';
      if (kind === 'ended_session') return 'Ended session';
      return 'Success';
    }
    case 'urge_logged': return 'Logged urge';
    case 'urge_episode_started': return 'Started urge support';
    case 'urge_episode_closed': {
      const outcome = payload['outcome'];
      if (outcome === 'passed') return 'Urge passed';
      if (outcome === 'used') return 'Logged use after support';
      return 'Closed urge';
    }
    case 'rescue_started': return 'Started a rescue';
    case 'rescue_completed': return 'Completed a rescue';
    case 'rescue_abandoned': return 'Ended rescue early';
    case 'urge_challenge_generated': return 'Generated a challenge';
    case 'urge_challenge_resolved': {
      const result = payload['result'];
      return result === 'met' ? 'Met a challenge' : 'Challenge — partial';
    }
    case 'purchase_logged': return 'Logged a purchase';
    case 'purchase_corrected': return 'Corrected a purchase';
    case 'purchase_deleted': return 'Deleted a purchase';
    case 'usage_session_ended': return 'Ended a session';
    case 'plan_adjusted': return 'Plan adjusted';
    case 'quota_period_closed': return 'Quota period closed';
    case 'window_day_closed': return 'Daily window closed';
    case 'backup_exported': return 'Exported a backup';
    case 'backup_imported': return 'Imported a backup';
    case 'onboarding_completed': return 'Plan created';
    default: return type.replace(/_/g, ' ');
  }
}
